import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Compass,
  HelpCircle,
  RotateCcw,
  Search,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useOnboarding } from '../onboarding/OnboardingContext';
import {
  getContextGreeting,
  getWhatNextAnswer,
  matchQuickPrompt,
  resolveAssistantRoute,
  searchFeatureGuide,
} from '../../content/bridgeAssistantContent';

function AssistantMessage({ text, actions, ctx, onNavigate }) {
  if (!text) return null;

  return (
    <div className="rounded-xl border border-[hsl(38_14%_88%)] bg-[hsl(42_28%_99%)] p-3.5 text-sm leading-relaxed text-[hsl(24_14%_18%)]">
      <p>{text}</p>
      {actions?.length ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {actions.map((action) => {
            const to = resolveAssistantRoute(action, ctx);
            return (
              <Button
                key={`${action.label}-${to}`}
                type="button"
                size="sm"
                variant="outline"
                className="h-8 rounded-full border-[hsl(38_14%_82%)] bg-white text-xs"
                onClick={() => onNavigate(to)}
              >
                {action.label}
              </Button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export function BridgeAssistantPanel({ open, onClose, ctx, quickPrompts }) {
  const navigate = useNavigate();
  const { requestWalkthroughReplay } = useOnboarding();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [activePromptId, setActivePromptId] = useState(null);
  const [searchResults, setSearchResults] = useState([]);

  const contextGreeting = getContextGreeting(ctx);

  const activePrompt = quickPrompts.find((p) => p.id === activePromptId);
  const activeAnswer = activePrompt?.dynamic === 'whatNext'
    ? getWhatNextAnswer(ctx)
    : activePrompt
      ? { text: activePrompt.answer, actions: activePrompt.actions }
      : null;

  useEffect(() => {
    if (!open) {
      setQuery('');
      setActivePromptId(null);
      setSearchResults([]);
      return;
    }
    const t = window.setTimeout(() => inputRef.current?.focus(), 120);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    setSearchResults(searchFeatureGuide(query));
  }, [query]);

  const goTo = useCallback(
    (to) => {
      onClose();
      navigate(to);
    },
    [navigate, onClose],
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const matched = matchQuickPrompt(query);
    if (matched) {
      setActivePromptId(matched.id);
      setSearchResults([]);
      return;
    }
    if (searchResults.length === 1) {
      const hit = searchResults[0];
      setActivePromptId(null);
      setQuery('');
      goTo(hit.route || `/guide#${hit.sectionId}`);
      return;
    }
    if (searchResults.length > 0) {
      return;
    }
    setActivePromptId('what-next');
  };

  const restartWalkthrough = () => {
    requestWalkthroughReplay();
    onClose();
    if (ctx.pathname !== '/dashboard') {
      navigate('/dashboard');
    }
  };

  if (!open) return null;

  return (
    <div
      className={cn(
        'fixed z-[55] flex w-[min(100vw-1.5rem,22rem)] flex-col overflow-hidden rounded-2xl',
        'border border-[hsl(38_14%_86%)] bg-[hsl(42_28%_98%)] shadow-[0_24px_64px_-16px_hsl(24_14%_12%_/_0.35)]',
        'animate-in fade-in-0 slide-in-from-bottom-3 duration-200',
        ctx.hasMobileBottomNav
          ? 'bottom-[calc(8.75rem+env(safe-area-inset-bottom,0px))] right-3 max-h-[min(58vh,28rem)]'
          : 'bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] right-4 max-h-[min(72vh,34rem)] sm:bottom-24 sm:right-6',
      )}
      role="dialog"
      aria-labelledby="bridge-assistant-title"
      aria-modal="true"
      data-testid="bridge-assistant-panel"
    >
      <header className="shrink-0 border-b border-[hsl(38_14%_90%)] bg-[hsl(152_28%_16%)] px-4 py-3.5 text-[hsl(42_28%_96%)]">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Compass className="h-4 w-4 shrink-0 text-[#c9a961]" strokeWidth={1.75} aria-hidden />
              <h2 id="bridge-assistant-title" className="font-serif text-base font-normal">
                Bridge Assistant
              </h2>
            </div>
            <p className="mt-1 text-xs leading-relaxed text-[hsl(42_28%_90%_/_0.85)]">
              Ask how to use MyLegacyBridge or find a feature.
            </p>
            {contextGreeting ? (
              <p className="mt-2 text-xs font-medium text-[#c9a961]/95">{contextGreeting}</p>
            ) : null}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 rounded-full text-[hsl(42_28%_92%)] hover:bg-white/10 hover:text-white"
            onClick={onClose}
            aria-label="Close Bridge Assistant"
            data-testid="bridge-assistant-close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
        <form onSubmit={handleSubmit} className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search features or ask a question…"
            className="h-10 rounded-xl border-[hsl(38_14%_86%)] bg-white pl-9 text-sm"
            data-testid="bridge-assistant-search"
          />
        </form>

        {searchResults.length > 0 && !activeAnswer ? (
          <ul className="mt-2 space-y-1" data-testid="bridge-assistant-search-results">
            {searchResults.map((hit) => (
              <li key={hit.id}>
                <button
                  type="button"
                  className="w-full rounded-lg px-2.5 py-2 text-left text-sm hover:bg-[hsl(42_26%_97%)]"
                  onClick={() => goTo(hit.route || `/guide#${hit.sectionId}`)}
                >
                  <span className="font-medium text-[hsl(24_14%_16%)]">{hit.name}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground line-clamp-2">{hit.howTo}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="mt-3 flex flex-wrap gap-1.5">
          {quickPrompts.map((prompt) => (
            <button
              key={prompt.id}
              type="button"
              className={cn(
                'rounded-full border px-2.5 py-1 text-[11px] leading-snug transition-colors',
                activePromptId === prompt.id
                  ? 'border-[hsl(152_28%_22%_/_0.35)] bg-[hsl(152_28%_22%_/_0.08)] text-[hsl(152_28%_22%)]'
                  : 'border-[hsl(38_14%_86%)] bg-white text-[hsl(32_10%_38%)] hover:border-[hsl(38_36%_42%_/_0.35)] hover:text-[hsl(24_14%_16%)]',
              )}
              onClick={() => {
                setActivePromptId(prompt.id);
                setQuery('');
                setSearchResults([]);
              }}
            >
              {prompt.label}
            </button>
          ))}
        </div>

        {activeAnswer ? (
          <div className="mt-3">
            <AssistantMessage
              text={activeAnswer.text}
              actions={activeAnswer.actions}
              ctx={ctx}
              onNavigate={goTo}
            />
          </div>
        ) : (
          <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
            Choose a prompt or search the feature guide. I help with navigation and product questions — not medical, legal or DNA interpretation.
          </p>
        )}
      </div>

      <footer className="shrink-0 border-t border-[hsl(38_14%_90%)] bg-[hsl(42_28%_96%)] px-3 py-2.5">
        <div className="flex flex-wrap gap-1.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 flex-1 rounded-full text-xs text-[hsl(32_10%_38%)]"
            onClick={() => goTo('/guide')}
          >
            <BookOpen className="mr-1.5 h-3.5 w-3.5" />
            Feature Guide
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 flex-1 rounded-full text-xs text-[hsl(32_10%_38%)]"
            onClick={restartWalkthrough}
          >
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
            Restart tour
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 flex-1 rounded-full text-xs text-[hsl(32_10%_38%)]"
            asChild
          >
            <Link to="/faq" onClick={onClose}>
              <HelpCircle className="mr-1.5 h-3.5 w-3.5" />
              FAQ
            </Link>
          </Button>
        </div>
      </footer>
    </div>
  );
}

export default BridgeAssistantPanel;
